import React, { useEffect } from 'react';
import {
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Grid,
    GridItem,
    HStack,
    Icon,
    Text,
    Box,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { EditUnitForm, EditUnitResource } from '@mark43/cad-api';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { HelpText } from '../common/components/help-text';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormTextarea } from '../forms/integrated-form-controls/form-text-area';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { agencyTypeIconNames } from '../common/helpers/agency-type';
import { AgencyTypeGlobal, globalAttributes } from '../common/global-attributes';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

export const EditTemporaryUnitDrawer = observer(function EditTemporaryUnitDrawer({
    unitId,
}: {
    unitId?: number;
}): React.ReactElement {
    const drawerControls = useDrawerControls();
    const closeDrawer = React.useMemo(() => {
        return () => {
            drawerControls.close();
        };
    }, [drawerControls]);
    return (
        <Drawer variant="overlay" isOpen={true} size="lg" onClose={closeDrawer}>
            <EditTemporaryUnitDrawerContent unitId={unitId} closeDrawer={closeDrawer} />
        </Drawer>
    );
});

export type EditTemporaryUnitDrawerType = typeof EditTemporaryUnitDrawer;

type EditTemporaryUnitForm = Pick<
    EditUnitForm,
    'comments' | 'unitId' | 'callSign' | 'dispatchArea' | 'staffCount'
>;

function EditTemporaryUnitDrawerContent({
    unitId: initialUnitId,
    closeDrawer,
}: {
    unitId?: number;
    closeDrawer: () => void;
}): React.ReactElement {
    const apiClient = useApiClient();
    const { staffCountEnabled } = useEnabledFeatures();
    const form = useForm<EditTemporaryUnitForm>({
        defaultValues: {
            comments: '',
            staffCount: undefined,
        },
        mode: 'onBlur',
    });

    const query = useQuery(['edit-unit-view', initialUnitId] as const, ({ queryKey }) => {
        return apiClient
            .request<EditUnitResource.GetEditUnitView>({
                method: 'POST',
                url: `edit_unit/${queryKey[1]}/`,
                data: {
                    formOpenedForUnitId: queryKey[1],
                },
            })
            .then((resp) => {
                return resp.data.data;
            });
    });

    const { data: editUnitView } = query;

    const mutation = useMutation(
        (editUnitForm: EditUnitForm) => {
            return apiClient.request<EditUnitResource.UpdateEditUnit>({
                method: 'POST',
                url: 'edit_unit/',
                data: editUnitForm,
            });
        },
        {
            onSuccess() {
                closeDrawer();
            },
        }
    );

    // Initial Hydration
    useEffect(
        function hydrateEditTempUnitForm() {
            if (editUnitView) {
                form.reset(editUnitView.form, { keepDefaultValues: true });
            }
        },
        [editUnitView, form]
    );

    const onSubmit = React.useCallback(
        (props: EditTemporaryUnitForm): void => {
            mutation.mutate({
                comments: props.comments,
                staffCount: props.staffCount,
                unitId: initialUnitId,
            } as EditUnitForm);
        },
        [mutation, initialUnitId]
    );

    return (
        <IntegratedFormProvider query={query} mutation={mutation} form={form} onSubmit={onSubmit}>
            <DrawerContent>
                <DrawerHeader>Edit Temporary Unit</DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <Grid
                            h="200px"
                            templateRows="repeat(3, 1fr)"
                            templateColumns="repeat(5, 1fr)"
                            gap={4}
                        >
                            <GridItem colSpan={5}>
                                <HStack>
                                    <Icon
                                        icon={
                                            agencyTypeIconNames[
                                                (editUnitView?.form.callSign.unitClass ??
                                                    globalAttributes.agencyTypeGlobal
                                                        .POLICE) as AgencyTypeGlobal
                                            ]
                                        }
                                    />
                                    <Text variant="headingSm">UNIT</Text>
                                </HStack>
                            </GridItem>
                            <GridItem colSpan={1}>
                                <Text variant="caption">Call Sign</Text>
                            </GridItem>
                            <GridItem colSpan={4}>
                                <HStack>
                                    <Text variant="caption">
                                        {form.getValues().callSign?.label}
                                    </Text>
                                    <HelpText overlay="To change the call sign, release this unit from the event and assign a new temporary unit with the desired call sign." />
                                </HStack>
                            </GridItem>
                            <GridItem colSpan={1}>
                                <Text variant="caption">D-Area</Text>
                            </GridItem>
                            <GridItem colSpan={4}>
                                <Text variant="caption">
                                    {form.getValues().dispatchArea?.label}
                                </Text>
                            </GridItem>
                            {staffCountEnabled && (
                                <GridItem colSpan={4}>
                                    <FormNumberInput
                                        name="staffCount"
                                        control={form.control}
                                        label="Unit Staff Count"
                                    />
                                </GridItem>
                            )}
                            <GridItem colSpan={5}>
                                <FormTextarea
                                    autoFocus={true}
                                    name="comments"
                                    label="Comments"
                                    rules={{
                                        maxLength: {
                                            message:
                                                'Comments cannot exceed 255 characters in length.',
                                            value: 255,
                                        },
                                    }}
                                    control={form.control}
                                />
                            </GridItem>
                        </Grid>
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    <Box mr={3}>
                        <FormCancelButton
                            onClick={() => {
                                closeDrawer();
                            }}
                        />
                    </Box>
                    <FormSaveButton />
                </DrawerFooter>
            </DrawerContent>
        </IntegratedFormProvider>
    );
}
